import { useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Save, Trash2 } from 'lucide-react';
import api from '../../utils/api';
import BuildingPageHeading from '../../components/BuildingPageHeading';
import InspectionPeriodMethodsTable from '../../components/InspectionPeriodMethodsTable';
import { resolveUnitOps } from '../../utils/unitDisplay';
import { parseInspectionPeriodMethods } from '../../utils/inspectionPeriodMethods';
import {
  parseUnitUniqueId,
  buildUnitUniqueId,
  formatUnitIdShort,
  isSuffixTaken,
} from '../../utils/unitIdFormat';

const FREQUENCY_OPTIONS = [
  { value: 'ANNUAL', label: 'Annual' },
  { value: 'SIX_MONTHLY', label: 'Six-monthly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'DAILY', label: 'Daily' },
];

const METHOD_OPTIONS = [
  { value: 'UNIFORM', label: 'Same method each inspection' },
  { value: 'VARIES_BY_PERIOD', label: 'Varies by period / schedule' },
];

/**
 * Floor-plan placed equipment unit (e.g. 9-2-1) ??edit details under a type parent (9-2).
 */
export default function EquipmentUnitDetail() {
  const { id: buildingId, unitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { data: building } = useQuery({
    queryKey: ['building', buildingId],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!buildingId,
  });

  const {
    data: unit,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['ss-unit', unitId],
    queryFn: async () => {
      const res = await api.get(`/ss-units/${unitId}`);
      return res.data?.data ?? res.data;
    },
    enabled: !!unitId,
  });

  const { data: siblingUnits = [] } = useQuery({
    queryKey: ['building', buildingId, 'ss-units', 'all'],
    queryFn: async () => {
      const res = await api.get(`/buildings/${buildingId}/ss-units`, {
        params: { includeInactive: '1' },
      });
      return res.data?.data ?? [];
    },
    enabled: !!buildingId,
  });

  const { data: levelsSetup } = useQuery({
    queryKey: ['building', buildingId, 'levels-setup'],
    queryFn: async () => (await api.get(`/buildings/${buildingId}/levels-setup`)).data?.data,
    enabled: !!buildingId,
  });

  const usedUniqueIds = useMemo(
    () => siblingUnits.map((u) => u.unique_id),
    [siblingUnits]
  );

  if (isLoading) {
    return (
      <div>
        <BuildingPageHeading buildingId={buildingId} title="Equipment unit" />
        <p className="text-gray-500 mt-6">Loading…</p>
      </div>
    );
  }

  if (error || !unit) {
    return (
      <div>
        <BuildingPageHeading buildingId={buildingId} title="Equipment unit" />
        <p className="text-red-600 mt-6">{error?.response?.data?.error || 'Unit not found'}</p>
      </div>
    );
  }

  return (
    <EquipmentUnitForm
      key={unit.id}
      buildingId={buildingId}
      building={building}
      unit={unit}
      usedUniqueIds={usedUniqueIds}
      levelsSetup={levelsSetup}
      siblingUnits={siblingUnits}
      navigate={navigate}
      location={location}
      queryClient={queryClient}
    />
  );
}

function EquipmentUnitForm({
  buildingId,
  building,
  unit,
  usedUniqueIds,
  levelsSetup,
  siblingUnits,
  navigate,
  location,
  queryClient,
}) {
  const ops = resolveUnitOps(unit);
  const parsedId = parseUnitUniqueId(unit.unique_id, unit.ss_code);
  const [idSuffix, setIdSuffix] = useState(parsedId.suffix || '1');
  const [form, setForm] = useState({
    name: unit.name || unit.unit_type || '',
    location: ops.location,
    manufacturer: unit.manufacturer || '',
    model: unit.model || '',
    serial_number: unit.serial_number || '',
    installation_year: unit.installation_year || '',
    specifications: unit.specifications || '',
    inspection_frequency: ops.inspection_frequency || 'ANNUAL',
    inspection_method_type: ops.inspection_method_type || 'UNIFORM',
    inspection_method_notes: ops.inspection_method_notes || '',
    level_id: unit.level_id || '',
  });
  const [periodMethods, setPeriodMethods] = useState(() =>
    parseInspectionPeriodMethods(unit.inspection_period_methods)
  );

  const parentFromState = location.state?.parentUnitId;
  const parentUnit = useMemo(() => {
    if (parentFromState) {
      return siblingUnits.find((u) => String(u.id) === String(parentFromState)) || null;
    }
    const parentUid = parsedId.prefix;
    return siblingUnits.find((u) => String(u.unique_id || '').trim() === parentUid) || null;
  }, [parentFromState, siblingUnits, parsedId.prefix]);

  const backToParent = () => {
    if (parentUnit?.id) {
      navigate(`/buildings/${buildingId}/units/${parentUnit.id}?tab=system-info`, {
        state: location.state?.origin
          ? { origin: location.state.origin }
          : undefined,
      });
      return;
    }
    const ss = unit.ss_code;
    navigate(
      ss
        ? `/buildings/${buildingId}/units?ss=${encodeURIComponent(ss)}`
        : `/buildings/${buildingId}/units`
    );
  };

  const suffixError = isSuffixTaken(parsedId.prefix, idSuffix, usedUniqueIds, unit.unique_id);
  const displayId = buildUnitUniqueId(parsedId.prefix, idSuffix) || unit.unique_id;
  const titleName = form.name || unit.unit_type || '';

  const invalidate = () => {
    queryClient.invalidateQueries(['ss-unit', unit.id]);
    queryClient.invalidateQueries(['building', buildingId, 'ss-units']);
  };

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const res = await api.put(`/ss-units/${unit.id}`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Equipment unit saved');
      invalidate();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to save'),
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/ss-units/${unit.id}`);
    },
    onSuccess: () => {
      toast.success('Equipment unit deleted');
      invalidate();
      backToParent();
    },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to delete'),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    const unique_id = buildUnitUniqueId(parsedId.prefix, idSuffix);
    if (!unique_id) {
      toast.error('Enter a valid unit number');
      return;
    }
    if (suffixError) {
      toast.error(`${formatUnitIdShort(unique_id)} is already used`);
      return;
    }
    saveMutation.mutate({
      ...form,
      unique_id,
      name: form.name || form.location || unique_id,
      installation_year: form.installation_year ? Number(form.installation_year) : null,
      level_id: form.level_id || null,
      inspection_period_methods:
        form.inspection_method_type === 'VARIES_BY_PERIOD' ? periodMethods : null,
    });
  };

  return (
    <div>
      <BuildingPageHeading
        buildingId={buildingId}
        buildingName={building?.name}
        title={`SS ${formatUnitIdShort(displayId, unit.ss_code)}${titleName ? ` ${titleName}` : ''}`}
        extraTrail={
          parentUnit
            ? [
                {
                  label: `SS ${formatUnitIdShort(parentUnit.unique_id, parentUnit.ss_code)}${
                    parentUnit.name ? ` ${parentUnit.name}` : ''
                  }`,
                  to: `/buildings/${buildingId}/units/${parentUnit.id}?tab=system-info`,
                },
              ]
            : undefined
        }
        unitsSsCode={unit.ss_code}
      />

      <form onSubmit={handleSave} className="mt-4 bg-card rounded-xl shadow p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit ID *</label>
            <div className="flex items-stretch">
              <span className="inline-flex items-center px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg text-sm font-mono text-gray-700">
                {parsedId.prefix}-
              </span>
              <input
                type="number"
                min={1}
                value={idSuffix}
                onChange={(e) => setIdSuffix(e.target.value)}
                required
                className={`flex-1 px-3 py-2 border rounded-r-lg font-mono ${
                  suffixError ? 'border-red-400' : 'border-gray-300'
                }`}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Prefix is fixed; only the number after the dash is editable.
            </p>
            {suffixError && (
              <p className="text-xs text-red-600 mt-1">This number is already used.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="Unit name"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Location label</label>
            <input
              name="location"
              value={form.location}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
            <input
              name="manufacturer"
              value={form.manufacturer}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
            <input
              name="model"
              value={form.model}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Serial number</label>
            <input
              name="serial_number"
              value={form.serial_number}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Level / floor</label>
            {(levelsSetup?.levels || []).length > 0 ? (
              <select
                name="level_id"
                value={form.level_id || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="">Unassigned</option>
                {(levelsSetup.levels || []).map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.block_label ? `${l.block_label} · ` : ''}
                    {l.label}
                    {l.plan_label ? ` (${l.plan_label})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              <p className="text-xs text-gray-500 py-2">
                No levels configured ?? set up under Building ??Floor plan ??Level plans.
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Installation year</label>
            <input
              name="installation_year"
              type="number"
              value={form.installation_year}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Inspection frequency</label>
            <select
              name="inspection_frequency"
              value={form.inspection_frequency}
              onChange={(e) => {
                setForm((p) => ({ ...p, inspection_frequency: e.target.value }));
                setPeriodMethods({});
              }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {FREQUENCY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Inspection method</label>
            <select
              name="inspection_method_type"
              value={form.inspection_method_type}
              onChange={(e) => {
                const v = e.target.value;
                setForm((p) => ({ ...p, inspection_method_type: v }));
                if (v === 'UNIFORM') setPeriodMethods({});
              }}
              className="w-full px-3 py-2 border rounded-lg"
            >
              {METHOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {form.inspection_method_type === 'UNIFORM' && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Inspection procedure
              </label>
              <textarea
                name="inspection_method_notes"
                value={form.inspection_method_notes}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          )}
          <InspectionPeriodMethodsTable
            frequency={form.inspection_frequency}
            methodType={form.inspection_method_type}
            value={periodMethods}
            onChange={setPeriodMethods}
          />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Specifications</label>
            <textarea
              name="specifications"
              value={form.specifications}
              onChange={handleChange}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
        </div>

        <div className="flex justify-between pt-2 border-t">
          <button
            type="button"
            onClick={() => {
              if (window.confirm(`Remove unit ${displayId}?`)) deleteMutation.mutate();
            }}
            disabled={deleteMutation.isPending}
            className="flex items-center px-4 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={backToParent} className="px-4 py-2 border rounded-lg">
              Back
            </button>
            <button
              type="submit"
              disabled={saveMutation.isPending || suffixError}
              className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </form>
      {building?.name ? (
        <p className="sr-only">{building.name}</p>
      ) : null}
    </div>
  );
}
